"use client";
/**
 * AddItemsModal3.jsx
 *
 * Vendor-filtered item search for EditMaterialOrder3.
 * Data source: GET /api/materialOrders3/:orderId/available-items
 *   — already scoped to the order's vendor by the backend.
 *
 * Scoring engine: unified (same as UnifiedItemSearch)
 *   Handles: steel studs/track (depth+gauge+leg), drywall, screws,
 *            insulation, compound types, dimensional phrases, aliases,
 *            AI intent for coded queries (158S125-18, 3-5/8 stud 25ga…)
 *
 * onSelect interface (unchanged — EditMaterialOrder3 receives this shape):
 *   onSelect({ item, qty, size, coveragePerUnit, unitPrice })
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ═══════════════════════════════════════════════════════════════════════════════
// ── COVERAGE HELPERS (exported — EditMaterialOrder3 imports these) ────────────
// ═══════════════════════════════════════════════════════════════════════════════

export const isCoverageBased = (uom = "") =>
  ["SF", "LF"].some((u) => uom.trim().toUpperCase().includes(u));

function tokenToFeet(s) {
  // Normalize smart/curly quotes and other inch-mark variants to ASCII "
  s = (s || "").trim().replace(/[\u201C\u201D\u02DD\u02BA\u2033\u22EE]/g, '"');
  if (!s) return null;
  let m = s.match(
    /^(\d+(?:\.\d+)?)'[\s]*(\d+(?:-\d+\/\d+)?(?:\/\d+)?|\d*-\d+\/\d+)"$/,
  );
  if (m) return parseFloat(m[1]) + inchStrToFeet(m[2]);
  m = s.match(/^(\d+(?:\.\d+)?)'$/);
  if (m) return parseFloat(m[1]);
  m = s.match(/^(\d+(?:-\d+\/\d+)?(?:\/\d+)?)"$/);
  if (m) return inchStrToFeet(m[1]);
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
}

function inchStrToFeet(s) {
  s = (s || "").trim();
  let m = s.match(/^(\d+)-(\d+)\/(\d+)$/);
  if (m) return (parseInt(m[1]) + parseInt(m[2]) / parseInt(m[3])) / 12;
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1]) / parseInt(m[2]) / 12;
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]) / 12;
  return 0;
}

export function parseSizeCoverage(size, uom) {
  if (!size || !uom) return null;
  const u = uom.trim().toUpperCase();
  const s = size.trim();
  if (u.includes("SF")) {
    const parts = s.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const a = tokenToFeet(parts[0].trim());
      const b = tokenToFeet(parts[1].trim());
      if (a !== null && b !== null) return Math.round(a * b * 10000) / 10000;
    }
    return null;
  }
  if (u.includes("LF")) {
    const ft = tokenToFeet(s);
    return ft !== null ? Math.round(ft * 10000) / 10000 : null;
  }
  return null;
}

const calcPackageCost = (rate, coverage) =>
  parseFloat(rate || 0) * (parseFloat(coverage) || 1);

// ═══════════════════════════════════════════════════════════════════════════════
// ── UNIFIED SCORING ENGINE ────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

// ── Normalization ─────────────────────────────────────────────────────────────
const QUOTE_RE = /['"\u2018\u2019\u201c\u201d\u2032\u2033`]/g;
const stripQ = (s) => String(s == null ? "" : s).replace(QUOTE_RE, "");
const FRAC_RE = /\d+[-–]\d+\/\d+|\d+\/\d+/g;
const normFrac = (s) =>
  (s || "").replace(FRAC_RE, (m) => m.replace(/[\s\-\/]/g, "_"));

function norm(s) {
  return normFrac(stripQ(s || ""))
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
function tok(s) {
  return norm(s)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);
}

// ── Steel framing tables ──────────────────────────────────────────────────────
const MIL2GA = { 18: "25", 33: "20", 43: "18", 54: "16", 68: "14", 97: "12" };

const WIDTH_SH = {
  158: 1.625,
  162: 1.625,
  212: 2.5,
  250: 2.5,
  312: 3.5,
  350: 3.5,
  358: 3.625,
  362: 3.625,
  400: 4,
  512: 5.5,
  550: 5.5,
  600: 6,
  800: 8,
  1000: 10,
  1200: 12,
};

const WEBCODE = {
  125: 1.25,
  137: 1.375,
  150: 1.5,
  162: 1.625,
  212: 2.5,
  250: 2.5,
  350: 3.5,
  358: 3.625,
  362: 3.625,
  400: 4,
  550: 5.5,
  600: 6,
  800: 8,
  1000: 10,
  1200: 12,
};

const TRACK_FAM = new Set(["track", "slotted track", "flex track", "j runner"]);

// ── Type detection ────────────────────────────────────────────────────────────
const TYPE_RULES = [
  [
    "insulation",
    [
      /insulation/,
      /comfortbatt/,
      /rockwool/,
      /roxul/,
      /mineral wool/,
      /safe.?n.?sound/,
      /\bbatt\b/,
      /fiberglass/,
    ],
  ],
  ["slotted track", [/slotted track/]],
  ["flex track", [/flex track/]],
  ["j runner", [/j-?\s*runner/]],
  ["ch stud", [/\bch stud/]],
  ["carrying channel", [/carrying channel/]],
  ["furring channel", [/furring/]],
  ["resilient channel", [/resilient/]],
  ["z bar", [/\bz ?bar/]],
  ["hanger wire", [/hanger wire/]],
  ["tie wire", [/tie wire/]],
  ["angle", [/\bangle\b/, /d700/, /\bl[ -]track\b/]],
  ["track", [/\btracks?\b/]],
  ["stud", [/\bstuds?\b/]],
  ["screw", [/screw/]],
  ["drywall", [/drywall/, /gypsum/, /\bdensglass\b/, /sheathing/]],
];

// ── Compound types ────────────────────────────────────────────────────────────
const COMPOUNDS = [
  ["z", "bar"],
  ["z", "furring"],
  ["hat", "channel"],
  ["resilient", "channel"],
  ["rc", "channel"],
  ["corner", "bead"],
  ["l", "bead"],
  ["j", "bead"],
  ["j", "trim"],
  ["cee", "stud"],
  ["drywall", "screw"],
  ["type", "s"],
  ["type", "x"],
  ["tek", "screw"],
  ["lay", "in"],
  ["drop", "ceiling"],
];

// ── Token aliases ─────────────────────────────────────────────────────────────
const ALIASES = {
  dw: ["drywall"],
  gb: ["gypsum"],
  gyp: ["gypsum"],
  std: ["stud"],
  mtl: ["metal"],
  ins: ["insulation"],
  trk: ["track"],
  cgc: ["cgc"],
  "25ga": ["25 ga", "25gauge"],
  "20ga": ["20 ga"],
  "18ga": ["18 ga"],
  "16ga": ["16 ga"],
  "14ga": ["14 ga"],
};

// ── Dimensional phrase patterns ───────────────────────────────────────────────
const DIM_DESCS =
  "leg|stud|track|flange|web|wing|return|lip|face|bar|channel|bead";
const DIM_PATS = [
  new RegExp(
    `(\\d+(?:[-–]\\d+\\/\\d+)?(?:\\/\\d+)?)["']?\\s*(${DIM_DESCS})`,
    "gi",
  ),
  /(\d+)\s*(ga|gauge|mil)\b/gi,
  /(\d+\/\d+)["']/gi,
];

function toVal(s) {
  const t = stripQ(String(s == null ? "" : s))
    .trim()
    .toLowerCase();
  let m = t.match(/^(\d+)[-\s](\d+)\/(\d+)$/);
  if (m) return +m[1] + +m[2] / +m[3];
  m = t.match(/^(\d+)\/(\d+)$/);
  if (m) return +m[1] / +m[2];
  m = t.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
}

const approx = (a, b, tol = 0.06) =>
  a != null && b != null && Math.abs(a - b) <= tol;

function detectType(text) {
  const t = norm(text);
  for (const [name, pats] of TYPE_RULES)
    if (pats.some((p) => p.test(t))) return name;
  return null;
}

function detectGauge(text) {
  const t = norm(text);
  let m = t.match(/(\d{1,2})\s*ga(?:uge)?\b/);
  if (m) return String(parseInt(m[1], 10));
  m = t.match(/(\d{2})\s*mil\b/);
  if (m) return MIL2GA[parseInt(m[1], 10)] || null;
  return null;
}

function detectLeg(text) {
  const m = norm(text).match(
    /(\d+(?:[-\s]\d+\/\d+|\/\d+)?)\s*(?:leg|flange|fl)\b/,
  );
  return m ? toVal(m[1]) : null;
}

function getItemSpec(item) {
  const code = stripQ(item.itemCode || "").toUpperCase();
  const desc =
    item.description || item.costCodeDescription || item.itemDescription || "";
  const spec = { depth: null, type: null, gauge: null, leg: null };
  const m = code.match(/^(\d{3,4})([ST])(\d{2,3})-(\d{2,3})/);
  if (m) {
    spec.depth = WEBCODE[parseInt(m[1], 10)] ?? null;
    spec.type = m[2] === "S" ? "stud" : "track";
    spec.leg = WEBCODE[parseInt(m[3], 10)] ?? null;
    spec.gauge = MIL2GA[parseInt(m[4], 10)] || null;
  }
  const dtype = detectType(desc);
  if (dtype) spec.type = dtype;
  if (spec.depth == null) {
    const md = desc
      .toLowerCase()
      .match(/^\s*(\d+(?:-\d+\/\d+)?)\s*["\u201d\u2033]/);
    if (md) spec.depth = toVal(md[1]);
  }
  if (spec.gauge == null) spec.gauge = detectGauge(desc);
  if (spec.leg == null) spec.leg = detectLeg(desc);
  return spec;
}

function getQuerySpec(q) {
  const spec = {
    depth: null,
    type: detectType(q),
    gauge: detectGauge(q),
    leg: detectLeg(q),
  };
  const stripped = norm(q)
    .replace(/(\d{1,2})\s*ga(?:uge)?\b/g, " ")
    .replace(/(\d{2})\s*mil\b/g, " ")
    .replace(/(\d+(?:[-\s]\d+\/\d+|\/\d+)?)\s*(?:leg|flange|fl)\b/g, " ");
  const tokens = tok(stripped);
  let digits = "";
  let i = 0;
  while (i < tokens.length && /^\d+$/.test(tokens[i])) {
    digits += tokens[i];
    i++;
  }
  if (digits) {
    spec.depth =
      WIDTH_SH[parseInt(digits, 10)] != null
        ? WIDTH_SH[parseInt(digits, 10)]
        : toVal(tokens[0]);
  }
  return spec;
}

function getCompounds(str) {
  const tokens = tok(str);
  const found = new Set();
  for (const [a, b] of COMPOUNDS) {
    for (let i = 0; i < tokens.length - 1; i++)
      if (tokens[i] === a && tokens[i + 1] === b) found.add(`${a} ${b}`);
    if (tokens.includes(`${a}${b}`)) found.add(`${a} ${b}`);
  }
  return found;
}

function getDimPhrases(str) {
  const n = norm(str);
  const phrases = new Set();
  for (const re of DIM_PATS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(n)) !== null) {
      const num = normFrac(m[1])
        .replace(/[\s\-\/]/g, "_")
        .toLowerCase();
      const desc = (m[2] || "").toLowerCase();
      const d = desc === "gauge" ? "ga" : desc === "mil" ? "mil" : desc;
      phrases.add(d ? `${num} ${d}` : num);
    }
  }
  return phrases;
}

function scoreItem(item, rawQuery, intent = null) {
  const qToks = tok(rawQuery);
  if (!qToks.length) return 0;
  const uniqueQ = [...new Set(qToks)];
  const qs = getQuerySpec(rawQuery);
  const isFraming = !!(qs.type || qs.gauge || qs.leg != null);
  let score = 0;

  // 1. Structured steel-framing
  if (isFraming) {
    const isp = getItemSpec(item);
    if (qs.type && isp.type) {
      if (qs.type === isp.type) score += 200;
      else if (TRACK_FAM.has(qs.type) && TRACK_FAM.has(isp.type)) score += 70;
      else score -= 220;
    } else if (qs.type && !isp.type) score -= 40;
    if (qs.depth != null && isp.depth != null)
      score += approx(qs.depth, isp.depth) ? 150 : -160;
    if (qs.gauge && isp.gauge) score += qs.gauge === isp.gauge ? 100 : -110;
    if (qs.leg != null && isp.leg != null)
      score += approx(qs.leg, isp.leg) ? 80 : -45;
    const specKeys = ["type", "depth", "gauge"].filter((k) => qs[k] != null);
    if (
      specKeys.length &&
      specKeys.every(
        (k) =>
          isp[k] != null &&
          (k === "depth" ? approx(qs[k], isp[k]) : qs[k] === isp[k]),
      )
    )
      score += 60;
  }

  // 2. AI intent
  if (intent) {
    const allText = norm(
      [
        item.itemCode,
        item.description,
        item.costCodeDescription,
        item.itemDescription,
        item.size,
        item.uom,
      ]
        .filter(Boolean)
        .join(" "),
    );
    const allToks = new Set(tok(allText));
    const reliable =
      intent.productType ||
      intent.gauge ||
      (intent.webSize && /[-\/]/.test(intent.webSize));
    if (reliable) {
      if (intent.webSize) {
        const ns = norm(intent.webSize);
        const hit =
          allText.includes(ns) ||
          tok(intent.webSize).every((t) => allToks.has(t));
        if (hit) score += 120;
        else if (/\d[_\-\/]\d/.test(allText)) score -= 80;
      }
      if (intent.productType) {
        const TYPE_KW = {
          stud: ["stud", "studs"],
          track: ["track", "tracks"],
          "slotted track": ["slotted", "sdt"],
          "z bar": ["z-bar", "z bar"],
          "furring channel": ["furring", "fch"],
          "resilient channel": ["resilient", "rch"],
          angle: ["angle"],
          screw: ["screw"],
          insulation: ["insulation", "rockwool", "comfortbatt"],
          drywall: ["drywall", "gypsum", "gyp"],
        };
        const kws = TYPE_KW[intent.productType] || [intent.productType];
        if (kws.some((kw) => allText.includes(norm(kw)))) score += 100;
        else score -= 70;
      }
      if (intent.gauge) {
        const GA_SIG = {
          25: ["-18", "25ga"],
          20: ["-33", "20ga"],
          18: ["-43", "18ga"],
          16: ["-54", "16ga"],
          14: ["-68", "14ga"],
          12: ["-97", "12ga"],
        };
        const sigs = GA_SIG[intent.gauge] || [`${intent.gauge}ga`];
        if (sigs.some((s) => allText.includes(norm(s)))) score += 100;
        else if (/\d{2}ga/.test(allText)) score -= 60;
      }
    }
  }

  // 3. Compound types
  const itemDesc =
    item.description || item.costCodeDescription || item.itemDescription || "";
  const qComps = getCompounds(rawQuery);
  const iComps = getCompounds(itemDesc);
  const compToks = new Set();
  for (const qc of qComps) {
    if (iComps.has(qc)) {
      score += 90;
      qc.split(" ").forEach((t) => compToks.add(t));
    } else score -= 40;
  }

  // 4. Dimensional phrases
  const qDim = getDimPhrases(rawQuery);
  const iDim = getDimPhrases(itemDesc);
  const qDescMap = new Map();
  let dimScore = 0;
  for (const qp of qDim) {
    const [val, desc] = qp.includes(" ") ? qp.split(" ") : [qp, ""];
    if (desc) qDescMap.set(desc, val);
    if (iDim.has(qp)) dimScore += 80;
  }
  for (const [desc, val] of qDescMap) {
    const hasSame = [...iDim].some((p) => p.endsWith(" " + desc));
    if (hasSame && !iDim.has(`${val} ${desc}`)) dimScore -= 60;
  }
  score += dimScore;

  // 5. Item-code exact / prefix
  const cn = norm(item.itemCode);
  const qn = norm(rawQuery);
  if (cn && qn === cn) score += 150;
  else if (cn && qn.length >= 3 && cn.startsWith(qn)) score += 80;

  // 6. Token-level fuzzy
  const hayAll = norm(
    [
      item.itemCode,
      item.matCode,
      item.description,
      item.costCodeDescription,
      item.itemDescription,
      item.size,
      item.uom,
      item.sectionNumber,
      item.bidArea,
    ]
      .filter(Boolean)
      .join(" "),
  );
  const haySet = new Set(tok(hayAll));
  const descToks = tok(norm(itemDesc));
  const matched = [];
  const unmatched = [];

  for (const qt of uniqueQ) {
    if (compToks.has(qt)) {
      matched.push(qt);
      continue;
    }
    if (/^\d{1,2}ga$/.test(qt)) continue;
    let hit = 0;
    const shortNum = /^\d{1,2}$/.test(qt);
    if (norm(item.itemCode || item.matCode || "") === qt) hit = 100;
    else if (
      !shortNum &&
      qt.length >= 3 &&
      norm(item.itemCode || "").startsWith(qt)
    )
      hit = 60;
    else if (descToks.includes(qt)) hit = shortNum ? 20 : 50;
    else if (haySet.has(qt)) hit = shortNum ? 12 : 30;
    else {
      let aliasHit = false;
      for (const [canonical, alts] of Object.entries(ALIASES)) {
        const forms = [canonical, ...alts].map(norm);
        if (
          forms.includes(qt) &&
          forms.some((f) => haySet.has(f) || hayAll.includes(f))
        ) {
          hit = 20;
          aliasHit = true;
          break;
        }
      }
      if (!aliasHit && !shortNum && qt.length >= 3 && hayAll.includes(qt))
        hit = 5;
    }
    if (hit > 0) {
      score += hit;
      matched.push(qt);
    } else unmatched.push(qt);
  }

  for (const ut of unmatched) {
    const isNum = /^\d+$/.test(ut);
    const isFrac = /_/.test(ut) || /\d+[-\/]\d/.test(ut);
    score -= (isNum ? 10 : 25) * (intent ? 0.4 : 1);
  }

  if (matched.length === uniqueQ.length) score += 30;
  const contentToks = uniqueQ.filter((t) => !/^\d+$/.test(t));
  if (
    contentToks.length > 0 &&
    contentToks.every((t) => descToks.includes(t) || compToks.has(t))
  )
    score += 40;

  return score;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── AI INTENT ─────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

const AI_TRIGGERS = [
  /(?<![a-zA-Z])\b(158|162|212|250|312|358|362|400|412|600|800|1000|1200)\b(?!\s*['"])/,
  /\b(\d+\/\d+|\d{3,})\s*(flange|leg|\bfl\b)/i,
  /\b\d+\s*mil\b/i,
];
const intentCache = new Map();

async function fetchIntent(q, token) {
  const key = q.trim().toLowerCase();
  if (intentCache.has(key)) return intentCache.get(key);
  if (key.length < 3 || !AI_TRIGGERS.some((re) => re.test(q))) return null;
  try {
    const res = await fetch(`${API}/aiSearch/parse-search-intent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ query: q.slice(0, 150) }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success) return null;
    intentCache.set(key, data.intent);
    return data.intent;
  } catch {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── UI ATOMS ──────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

function Highlight({ text = "", tokens = [] }) {
  if (!tokens.length) return <span>{text}</span>;
  const lower = text.toLowerCase();
  const ranges = [];
  for (const t of tokens) {
    const tn = norm(t);
    let idx = 0;
    while ((idx = lower.indexOf(tn, idx)) !== -1) {
      ranges.push([idx, idx + tn.length]);
      idx += tn.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1])
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    else merged.push([s, e]);
  }
  const parts = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark
        key={s}
        className="bg-orange-500/30 text-orange-200 rounded-sm px-0.5"
      >
        {text.slice(s, e)}
      </mark>,
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span>{parts}</span>;
}

function InlineInput({
  value,
  onChange,
  type = "text",
  className = "",
  ...rest
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={`px-1.5 py-0.5 text-xs bg-gray-700 border border-gray-600 rounded
                  text-gray-200 focus:border-orange-500 focus:outline-none ${className}`}
      {...rest}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

export default function AddItemsModal3({
  orderId,
  onSelect,
  onClose,
  showAlert,
}) {
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [intent, setIntent] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);
  const [addedItems, setAddedItems] = useState(new Map()); // id → count
  const [activeIdx, setActiveIdx] = useState(0);
  const [overrides, setOverrides] = useState({}); // { [id]: { size, rate, qty } }

  const inputRef = useRef(null);
  const rowRefs = useRef({});

  // ── Load vendor-filtered items ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API}/materialOrders3/${orderId}/available-items`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCatalogItems(res.data.items || []);
      } catch (err) {
        showAlert(
          "error",
          "Load Error",
          err.response?.data?.message || err.message,
        );
      } finally {
        setLoading(false);
      }
    })();
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [orderId]);

  // ── AI intent (debounced 420 ms) ──────────────────────────────────────────
  useEffect(() => {
    if (!query.trim() || query.trim().length < 3) {
      setIntent(null);
      return;
    }
    const t = setTimeout(async () => {
      setIntentLoading(true);
      const token = localStorage.getItem("token");
      setIntent(await fetchIntent(query, token));
      setIntentLoading(false);
    }, 420);
    return () => clearTimeout(t);
  }, [query]);

  const getOv = (id, field, def) => overrides[id]?.[field] ?? def;
  const setOv = (id, field, value) =>
    setOverrides((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));

  const queryToks = useMemo(() => tok(query), [query]);

  // ── Score ─────────────────────────────────────────────────────────────────
  const scored = useMemo(() => {
    if (!queryToks.length) {
      return catalogItems
        .map((item) => ({ item, score: 0 }))
        .sort((a, b) =>
          (a.item.itemCode || "").localeCompare(b.item.itemCode || ""),
        );
    }
    return catalogItems
      .map((item) => ({ item, score: scoreItem(item, query, intent) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [catalogItems, queryToks, query, intent]);

  useEffect(() => setActiveIdx(0), [scored]);

  // ── Add handler ───────────────────────────────────────────────────────────
  const handleAdd = useCallback(
    (item) => {
      const isCov = isCoverageBased(item.uom);
      const size = String(getOv(item._id, "size", item.size || "")).trim();
      const rate =
        parseFloat(getOv(item._id, "rate", item.unitPrice || 0)) || 0;
      const qty = parseFloat(getOv(item._id, "qty", 1)) || 1;
      const parsedCov = isCov ? parseSizeCoverage(size, item.uom) : null;
      const catalogCov = parseFloat(item.coveragePerUnit) || 1;
      const coverage = isCov
        ? parsedCov !== null
          ? parsedCov
          : catalogCov
        : 1;
      const unitPrice = isCov ? calcPackageCost(rate, coverage) : rate;

      // Same shape as before — EditMaterialOrder3.handleAddSingleItem expects this
      onSelect({
        item,
        qty,
        size,
        coveragePerUnit: isCov ? coverage : 0,
        unitPrice,
      });

      setAddedItems((prev) => {
        const next = new Map(prev);
        next.set(item._id, (next.get(item._id) || 0) + 1);
        return next;
      });
    },
    [overrides, onSelect],
  );

  const scrollTo = (idx) =>
    rowRefs.current[idx]?.scrollIntoView({ block: "nearest" });

  const handleKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.min(i + 1, scored.length - 1);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.max(i - 1, 0);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "Enter" && scored[activeIdx]) {
      handleAdd(scored[activeIdx].item);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const totalAdded = useMemo(
    () => [...addedItems.values()].reduce((s, n) => s + n, 0),
    [addedItems],
  );

  const intentSummary = intent
    ? [
        intent.productType,
        intent.webSize,
        intent.gauge && `${intent.gauge}ga`,
        intent.flange,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div
      className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onKeyDown={handleKey}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full flex flex-col max-h-[90vh]"
        style={{ maxWidth: "1200px" }}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-teal-500/20 border border-teal-500/30 flex items-center justify-center shrink-0">
              <svg
                className="w-3.5 h-3.5 text-teal-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">
                Add Items to Order
              </h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Vendor catalog · SF/LF: enter size → coverage auto-calculates ·
                modal stays open
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-700 transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* ── Search bar ── */}
        <div className="px-6 py-3 border-b border-gray-700/60 shrink-0">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Code, description, size, gauge… e.g. "3-5/8 stud 25ga", "158S125-18", "5/8 drywall", "resilient channel"'
              className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-800 border border-gray-600 rounded-xl text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery("");
                  setIntent(null);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-sm"
              >
                ×
              </button>
            )}
          </div>

          {/* Token + AI status */}
          <div className="flex items-center justify-between mt-1.5 gap-2">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              {queryToks.map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 text-[9px] font-mono bg-gray-800 text-gray-400 rounded border border-gray-700"
                >
                  {t}
                </span>
              ))}
              {intentLoading && (
                <span className="text-[10px] text-orange-400 animate-pulse font-medium">
                  AI parsing…
                </span>
              )}
              {intentSummary && !intentLoading && (
                <span className="text-[10px] text-orange-300 font-semibold px-2 py-0.5 bg-orange-900/30 border border-orange-800/50 rounded-full">
                  ⚡ {intentSummary}
                </span>
              )}
            </div>
            <span className="text-[10px] text-gray-600 shrink-0">
              {loading
                ? "Loading…"
                : `${scored.length} of ${catalogItems.length} items · ↑↓ · ↵ add`}
            </span>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-16 gap-3">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-teal-500" />
              <span className="text-gray-500 text-sm">
                Loading vendor items…
              </span>
            </div>
          )}

          {!loading && catalogItems.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-16">
              No catalog items found for this vendor.
            </p>
          )}

          {!loading && catalogItems.length > 0 && scored.length === 0 && (
            <div className="flex flex-col items-center py-16 gap-2 text-gray-500">
              <p className="text-sm">No items match "{query}"</p>
              <button
                onClick={() => setQuery("")}
                className="text-xs text-orange-400 hover:text-orange-300"
              >
                Clear search
              </button>
            </div>
          )}

          {!loading && scored.length > 0 && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-gray-900 border-b border-gray-700/70">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wider w-28">
                    Code
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-3 py-2.5 text-left text-[10px] text-gray-500 font-medium uppercase tracking-wider w-32">
                    Size{" "}
                    <span className="text-teal-500/60 normal-case font-normal">
                      (SF/LF)
                    </span>
                  </th>
                  <th className="px-3 py-2.5 text-center text-[10px] text-gray-500 font-medium uppercase tracking-wider w-14">
                    UOM
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wider w-24">
                    Coverage
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wider w-28">
                    Rate{" "}
                    <span className="text-teal-500/60 normal-case font-normal">
                      edit
                    </span>
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wider w-24">
                    $/PC
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wider w-20">
                    Qty{" "}
                    <span className="text-teal-500/60 normal-case font-normal">
                      edit
                    </span>
                  </th>
                  <th className="px-3 py-2.5 text-right text-[10px] text-gray-500 font-medium uppercase tracking-wider w-24">
                    Line Total
                  </th>
                  <th className="px-2 py-2.5 w-24" />
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-800">
                {scored.map(({ item }, idx) => {
                  const isActive = idx === activeIdx;
                  const isCov = isCoverageBased(item.uom);
                  const displayCode = item.itemCode || item.matCode || "—";
                  const displayDesc =
                    item.description ||
                    item.costCodeDescription ||
                    item.itemDescription ||
                    "—";

                  const ovSize = getOv(item._id, "size", item.size || "");
                  const ovRate = getOv(item._id, "rate", item.unitPrice || 0);
                  const ovQty = getOv(item._id, "qty", 1);

                  const parsedCov = isCov
                    ? parseSizeCoverage(ovSize, item.uom)
                    : null;
                  const catalogCov = parseFloat(item.coveragePerUnit) || 1;
                  const coverage = isCov
                    ? parsedCov !== null
                      ? parsedCov
                      : catalogCov
                    : 1;
                  const covParsed = parsedCov !== null;
                  const perPcPrice = isCov
                    ? calcPackageCost(ovRate, coverage)
                    : parseFloat(ovRate) || 0;
                  const lineTotal = (parseFloat(ovQty) || 0) * perPcPrice;
                  const addCount = addedItems.get(item._id) || 0;

                  return (
                    <tr
                      key={item._id}
                      ref={(el) => {
                        rowRefs.current[idx] = el;
                      }}
                      onMouseEnter={() => setActiveIdx(idx)}
                      className={`transition-colors group ${
                        isActive
                          ? "bg-orange-500/10 border-l-2 border-l-orange-500"
                          : idx % 2 === 0
                            ? "bg-gray-900/40 hover:bg-gray-800/60"
                            : "bg-gray-800/20 hover:bg-gray-800/60"
                      }`}
                    >
                      {/* Code */}
                      <td className="px-3 py-2 font-mono">
                        <span className="text-orange-400 font-medium text-[11px]">
                          <Highlight text={displayCode} tokens={queryToks} />
                        </span>
                      </td>

                      {/* Description */}
                      <td className="px-3 py-2">
                        <p className="text-gray-100 text-[12px] leading-snug font-medium">
                          <Highlight text={displayDesc} tokens={queryToks} />
                        </p>
                        {isCov && (
                          <p className="text-[9px] text-blue-400/70 mt-0.5">
                            Coverage-based · ordered by PC
                          </p>
                        )}
                      </td>

                      {/* Size */}
                      <td className="px-3 py-2">
                        {isCov ? (
                          <div className="flex flex-col gap-0.5">
                            <InlineInput
                              value={ovSize}
                              onChange={(v) => setOv(item._id, "size", v)}
                              className={`w-full ${ovSize ? (covParsed ? "border-green-600/60" : "border-orange-600/60") : ""}`}
                              placeholder={
                                item.uom?.toUpperCase().includes("SF")
                                  ? "e.g. 4'x8'"
                                  : "e.g. 10'"
                              }
                            />
                            {ovSize && !covParsed && (
                              <span className="text-[9px] text-orange-400">
                                Can't parse size
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-500 text-[11px]">
                            {item.size || "—"}
                          </span>
                        )}
                      </td>

                      {/* UOM */}
                      <td className="px-3 py-2 text-center">
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400">
                          {isCov ? "PC" : item.uom || "—"}
                        </span>
                        {isCov && (
                          <div className="text-[9px] text-gray-600 mt-0.5">
                            {item.uom}/PC
                          </div>
                        )}
                      </td>

                      {/* Coverage */}
                      <td className="px-3 py-2 text-right">
                        {isCov ? (
                          <div
                            className={`px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold border text-right ${
                              covParsed
                                ? "bg-blue-900/30 border-blue-700/40 text-blue-200"
                                : "bg-gray-700/40 border-gray-600 text-gray-500"
                            }`}
                          >
                            {covParsed ? `${coverage} ${item.uom}` : "—"}
                          </div>
                        ) : (
                          <span className="text-gray-700">—</span>
                        )}
                      </td>

                      {/* Rate */}
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <div className="flex items-center gap-1 justify-end">
                            <span className="text-gray-500 text-[10px]">$</span>
                            <InlineInput
                              type="number"
                              value={ovRate}
                              onChange={(v) => setOv(item._id, "rate", v)}
                              className="w-20 text-right"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <span className="text-[9px] text-gray-600">
                            /{isCov ? item.uom : item.uom || "PC"}
                          </span>
                        </div>
                      </td>

                      {/* $/PC */}
                      <td className="px-3 py-2 text-right font-mono">
                        {isCov ? (
                          <div className="flex flex-col items-end">
                            <span className="text-teal-300 font-semibold">
                              ${fmt(perPcPrice)}
                            </span>
                            {covParsed && (
                              <span className="text-[9px] text-gray-600 font-mono">
                                {coverage}×${fmt(parseFloat(ovRate) || 0)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300">
                            ${fmt(perPcPrice)}
                          </span>
                        )}
                      </td>

                      {/* Qty */}
                      <td className="px-3 py-2 text-right">
                        <InlineInput
                          type="number"
                          value={ovQty}
                          onChange={(v) => setOv(item._id, "qty", v)}
                          className="w-16 text-right"
                          min="1"
                          step="1"
                        />
                      </td>

                      {/* Line total */}
                      <td className="px-3 py-2 text-right font-mono">
                        {lineTotal > 0 ? (
                          <span className="text-blue-300 font-semibold">
                            ${fmt(lineTotal)}
                          </span>
                        ) : (
                          <span className="text-gray-700">—</span>
                        )}
                      </td>

                      {/* Add button */}
                      <td className="px-2 py-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAdd(item);
                            }}
                            className={`px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-all ${
                              addCount > 0
                                ? "bg-teal-600 hover:bg-teal-500 text-white"
                                : isActive
                                  ? "bg-orange-500 text-white"
                                  : "bg-gray-700 text-gray-400 group-hover:bg-orange-500 group-hover:text-white"
                            }`}
                          >
                            {addCount > 0 ? "+ Again" : "Add"}
                          </button>
                          {addCount > 0 && (
                            <span className="text-[9px] text-teal-400 font-semibold leading-none">
                              ✓ ×{addCount}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 py-3 border-t border-gray-700 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-4 text-[10px] text-gray-600">
            {[
              ["↑↓", "navigate"],
              ["↵", "add"],
              ["Esc", "close"],
            ].map(([key, label]) => (
              <span key={key}>
                <kbd className="px-1 py-0.5 bg-gray-800 border border-gray-700 rounded font-mono text-[9px] text-gray-500">
                  {key}
                </kbd>{" "}
                {label}
              </span>
            ))}
            {query && (
              <span className="text-gray-700">
                · AI scoring active for stud codes &amp; mil specs
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {totalAdded > 0 && (
              <span className="text-xs text-teal-400 font-medium">
                {totalAdded} item{totalAdded !== 1 ? "s" : ""} added
              </span>
            )}
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors"
            >
              {totalAdded > 0 ? "Done" : "Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
